import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  FaHome,
  FaProjectDiagram,
  FaMicrochip,
  FaCubes,
  FaEnvelope,
  FaHome as FaHomeIcon
} from "react-icons/fa";
import { useSpring, animated, config, useSpringRef, useChain } from 'react-spring'; // Added new imports
// Language-related imports commented out as everything will be in English
// import { useTranslation } from 'react-i18next';
// import LanguageSwitcher from './LanguageSwitcher';

// Create a NavbarContext to share animation state
import { createContext, useContext } from 'react';

export const NavbarAnimationContext = createContext({
  navbarShouldAnimate: false,
  setNavbarShouldAnimate: () => { },
});

export const useNavbarAnimation = () => useContext(NavbarAnimationContext);

const Navbar = () => {
  // Use static text instead of translation
  // const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isContactActive, setIsContactActive] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef();
  const firstRenderRef = useRef(true);

  // Get animation state from context
  const { navbarShouldAnimate } = useNavbarAnimation();

  // React Spring animation for navbar entrance
  const navbarAnimation = useSpring({
    from: { opacity: 0, transform: 'translateY(-100%)' },
    to: {
      opacity: 1,
      transform: navbarShouldAnimate ? 'translateY(0%)' : 'translateY(-100%)'
    },
    config: { ...config.gentle, tension: 120, friction: 14 },
  });

  // Colors for consistent theming
  const primaryTextColor = "text-primary"; // Using our new theme colors
  const secondaryTextColor = "text-[#212529]/80"; // Medium gray

  // React Spring refs for animation sequencing
  const blinkRef = useSpringRef();
  const typingRef = useSpringRef();

  // Blinking cursor animation
  const blinkProps = useSpring({
    ref: blinkRef,
    from: { opacity: 0 },
    to: [{ opacity: 1 }, { opacity: 0 }, { opacity: 1 }, { opacity: 0 }, { opacity: 1 }, { opacity: 0 }],
    config: { duration: 350 },
    delay: 300,
    onRest: () => {
      if (firstRenderRef.current) {
        typingRef.start();
      }
    }
  });

  // Logo text typing animation
  const typingProps = useSpring({
    ref: typingRef,
    from: { width: 0, display: "inline-block" },
    to: { width: 120, display: "inline-block" },  // Approximate width of "AXIALIS"
    delay: 500,
    config: { tension: 300, friction: 40 },
    onRest: () => {
      setAnimationComplete(true);
      firstRenderRef.current = false;
    }
  });

  // Chain the animations
  useChain(firstRenderRef.current ? [blinkRef, typingRef] : [], [0, 0.8]);

  // Helper function to determine if a route is active
  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const toggleMenu = () => setIsOpen(!isOpen);

  useEffect(() => {
    if (location.pathname === "/contact") {
      setIsContactActive(true);
    } else {
      setIsContactActive(false);
    }
  }, [location]);

  useEffect(() => {
    if (location.hash === "#contact-section") {
      const contactSection = document.getElementById("contact-section");
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        // Close main mobile menu on outside click if needed
        // setIsOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const closeMenu = () => {
    setIsOpen(false);
  };

  const handleNavLinkClick = (to) => {
    if (location.pathname === to) {
      window.scrollTo(0, 0);
    } else {
      navigate(to);
    }
    closeMenu();
  };

  const handleLogoClick = () => {
    if (location.pathname === "/") {
      window.scrollTo(0, 0);
    } else {
      navigate("/");
    }
    closeMenu();
  };

  // Updated NavLink for mobile
  const NavLinkWithClose = ({ to, children }) => (
    <div
      onClick={() => handleNavLinkClick(to)}
      className={`flex items-center pl-3 pr-4 py-2 border-l-4 ${location.pathname === to
        ? "border-primary text-primary bg-white/50 backdrop-blur-sm"
        : "border-transparent text-[#212529]/70 hover:bg-white/30 hover:backdrop-blur-sm hover:border-primary-light"
        } text-base font-nav font-nav uppercase tracking-nav cursor-pointer transition-all duration-300`}
    >
      {children}
    </div>
  );

  // Create a better styled NavLink component for desktop
  const DesktopNavLink = ({ to, children }) => {
    const active = isActive(to);
    return (
      <div
        onClick={() => handleNavLinkClick(to)}
        className="relative group"
      >
        <div className={`inline-flex items-center px-1 py-2 text-nav font-medium uppercase tracking-nav cursor-pointer transition-colors duration-300 ${active ? "text-primary" : "text-[#212529]"
          }`}>
          {children}
        </div>
        {/* Active indicator - animated underline */}
        <div className={`absolute bottom-0 left-0 w-full h-0.5 bg-primary transform transition-all duration-300 ${active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-75"
          }`} />
      </div>
    );
  };

  return (
    <animated.nav
      ref={navRef}
      style={navbarAnimation}
      className="fixed w-full z-[60] transition-all duration-300"
    >
      {/* Background with glossy effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#e9ecef]/80 to-[#dee2e6]/80 backdrop-blur-sm border-b border-white/40 shadow-sm -z-10"></div>

      {/* Light effects */}
      <div className="absolute -top-24 -left-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main flex container for desktop */}
        <div className="flex justify-between items-center h-16">
          {/* Left side: Logo Text Only */}
          <div className="flex items-center">
            <div
              className="flex-shrink-0 cursor-pointer flex items-center"
              onClick={handleLogoClick}
            >
              <div className="relative">
                {firstRenderRef.current ? (
                  <div className="relative flex items-center">
                    <animated.span style={typingProps} className="text-2xl font-display font-bold text-[#212529] uppercase tracking-logo overflow-hidden whitespace-nowrap">
                      AXIALIS
                    </animated.span>
                    <animated.span
                      style={blinkProps}
                      className="absolute right-0 top-0 h-full w-0.5 bg-black"
                    ></animated.span>
                  </div>
                ) : (
                  <span className="text-2xl font-display font-bold text-[#212529] uppercase tracking-logo">
                    AXIALIS
                  </span>
                )}
              </div>
            </div>
            {/* Nav Links moved to the right side */}
          </div>

          {/* Right side: Nav Links (Desktop) */}
          <div className="hidden md:flex md:items-center space-x-8">
            {/* Desktop Nav Links Container */}
            <div className="flex items-center space-x-8">
              {/* Nav Links with active indicator */}
              <DesktopNavLink to="/">Home</DesktopNavLink>
              <DesktopNavLink to="/projects">Projects</DesktopNavLink>
              <DesktopNavLink to="/technology">Technology</DesktopNavLink>
              <DesktopNavLink to="/applications">Applications</DesktopNavLink>
              <DesktopNavLink to="/explore-motor">Explore the Motor</DesktopNavLink>
              <DesktopNavLink to="/about">About Us</DesktopNavLink>
              <DesktopNavLink to="/contact">Contact</DesktopNavLink>
            </div>

            {/* Language Switcher removed */}
          </div>

          {/* Mobile Menu Button Area */}
          <div className="-mr-2 flex items-center md:hidden">
            {/* Language Switcher removed */}
            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className={`inline-flex items-center justify-center p-2 rounded-md text-[#212529]/80 hover:text-[#212529] focus:outline-none bg-white/30 backdrop-blur-sm shadow-sm border border-white/40 hover:bg-white/40 transition-all duration-300`}
              aria-label="Main menu"
              aria-expanded={isOpen}
            >
              {/* Hamburger/Close Icon */}
              <svg
                className={`h-6 w-6 transition-transform transform ${isOpen ? "rotate-45" : "rotate-0"}`}
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d={isOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden ${isOpen ? "block shadow-normal border-t border-white/20 backdrop-blur-sm bg-[#e9ecef]/60" : "hidden"}
          transition-all duration-300 ease-in-out`}
      >
        <div className="pt-2 pb-3 space-y-1">
          <NavLinkWithClose to="/">
            Home
          </NavLinkWithClose>
          <NavLinkWithClose to="/projects">
            Projects
          </NavLinkWithClose>
          <NavLinkWithClose to="/technology">
            Technology
          </NavLinkWithClose>
          <NavLinkWithClose to="/applications">
            Applications
          </NavLinkWithClose>
          <NavLinkWithClose to="/explore-motor">
            Explore the Motor
          </NavLinkWithClose>
          <NavLinkWithClose to="/about">
            About Us
          </NavLinkWithClose>
          <NavLinkWithClose to="/contact">
            Contact
          </NavLinkWithClose>
        </div>
      </div>
    </animated.nav>
  );
};

export default Navbar;